/**
 * Entry menu (DESIGN-client §7, "Web menu"): the web-only first screen — logo lockup, Start game,
 * Join game with the code field, and the quiet footer. Extracted from `Lobby.tsx`, which still
 * owns every handler and the table branches and renders this in the `!isDiscord && !table` case.
 *
 * Why it is a screen of its own rather than another `.screen__inner` column: this is the one piece
 * of UI that decides whether somebody plays at all, so it wants the whole viewport — a full-bleed
 * room behind it, a lamp pool, a wordmark with room to breathe — and `.screen` exists to centre a
 * 560px column of controls. Nothing here is Discord-facing; the activity opens straight into a
 * channel table and never sees this file.
 *
 * The atmosphere is the game's own diorama art (`scene/assets/diorama.ts`, `public/assets/diorama`)
 * reused as CSS backgrounds: the walnut room plate, the felt table, the lamp glow. No new art, no
 * new fonts, no library. Every plate is decorative — a 404 leaves the gradient underneath, which is
 * already a warm dark room — so a build without the art produced still looks deliberate.
 */
import { useEffect, useId, useRef, useState } from 'react';
import { isEmbedded, netBase } from '../../bridge/embedded';
import type { Player } from '../../net/types-local';
import { CreditsSheet } from '../sheets/Credits';
import { ProfileSheet } from '../sheets/Profile';
import { RulesSheet } from '../sheets/Rules';
import { Avatar, Button } from '../ui';
import { LevelBadge } from '../ui/LevelBadge';
import { VolumeControl } from '../ui/VolumeControl';

/**
 * Dust, and only dust.
 *
 * Three floating card silhouettes used to hang here too. Drawn in CSS they read as translucent
 * glass panels rather than as cards, parked at arbitrary points of a room they did not belong to,
 * and they were the third and fourth independent motion cue in a composition whose whole appeal is
 * restraint. They come back when there is card-back art to draw them from.
 */
const MOTES = [0, 1, 2, 3, 4, 5, 6, 7] as const;

/**
 * The room behind the menu and the boot screen.
 *
 * Split out and exported because Boot shows the same thing: a player who watches the bar fill on
 * one room and then lands on a different one has been shown two games. The plate URLs are built
 * from `netBase()` rather than written into the stylesheet so the Discord activity's `/.proxy`
 * prefix is honoured — Boot does run there.
 */
export function EntryAtmosphere() {
  const dir = `${netBase()}/assets/diorama/land`;
  const vars = {
    ['--entry-room' as string]: `url("${dir}/bg.webp")`,
    ['--entry-table' as string]: `url("${dir}/table.webp")`,
  };
  return (
    <div className="entry-atmos" style={vars} aria-hidden>
      <div className="entry-atmos__room" />
      <div className="entry-atmos__fill" />
      <div className="entry-atmos__table" />
      {/* Lamplight, felt bounce and the pool under the lockup, in one element rather than three. */}
      <div className="entry-atmos__light" />
      <div className="entry-atmos__dust">
        {MOTES.map((i) => (
          <i key={i} className="entry-atmos__mote" />
        ))}
      </div>
      <div className="entry-atmos__vignette" />
    </div>
  );
}

/**
 * The wordmark.
 *
 * A drawn logo (`public/assets/logo.webp`, 862x198 with alpha), not type. It replaced a CSS lockup —
 * a spade crest over "SPADES" and a letter-spaced "NIGHT" in the display face with a gradient
 * clipped to it — which was competent and completely generic. A brand is a drawing; the best CSS
 * can do is a well-set font, and a well-set font is what every other card game on the store has.
 *
 * `width`/`height` are on the element so the lockup reserves its box before the image lands and the
 * screen does not reflow around it mid-boot. The glow is a `drop-shadow` chain on the img, which
 * follows the alpha of the letters rather than lighting a rectangle behind them.
 *
 * `home` makes it the way back to the landing site (`/`) on the web menu. Nothing else on the
 * screen says "leave", and a wordmark that goes home is the convention everybody already knows.
 * Never inside Discord, where `/` is the Activity itself.
 */
export function EntryLogo({ tagline = 'Classic spades. Good company.', home }: { tagline?: string; home?: string }) {
  const img = <img className="entry__logo-img" src={`${netBase()}/assets/logo.webp`} width={862} height={198} alt="Spades Night" />;
  return (
    <div className="entry__logo">
      <h1 className="entry__wordmark">
        {home ? (
          <a className="entry__home" href={home} data-testid="entry-home">
            {img}
          </a>
        ) : (
          img
        )}
      </h1>
      <p className="entry__tagline">{tagline}</p>
    </div>
  );
}

export interface EntryMenuProps {
  me: Player | null;
  speaking?: boolean;
  muted?: boolean;
  /**
   * The badge on the identity pill, or null for none. Passed in rather than read from the store so
   * this screen stays a pure function of its props — the lobby owns every piece of state it shows.
   */
  level?: number | null;
  /** A network call is in flight; both actions are inert until it settles. */
  busy: boolean;
  /** The runtime is up. Nothing here can connect before it is. */
  ready: boolean;
  joinOpen: boolean;
  code: string;
  onToggleJoin: () => void;
  onCodeChange: (code: string) => void;
  onQuickPlay: () => void;
  onJoin: () => void;
  onOpenOptions: () => void;
}

export function EntryMenu({
  me,
  speaking,
  muted,
  level = null,
  busy,
  ready,
  joinOpen,
  code,
  onToggleJoin,
  onCodeChange,
  onQuickPlay,
  onJoin,
  onOpenOptions,
}: EntryMenuProps) {
  const [creditsOpen, setCreditsOpen] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const codeRef = useRef<HTMLInputElement>(null);
  const codeId = useId();
  const disabled = busy || !ready;

  // Opening the panel and then asking the player to find the field is two gestures for one
  // intention. The focus is deferred a frame so it lands after the panel's entrance animation
  // has a box to be focused in.
  useEffect(() => {
    if (!joinOpen) return;
    const id = window.setTimeout(() => codeRef.current?.focus(), 60);
    return () => window.clearTimeout(id);
  }, [joinOpen]);

  return (
    <div className="entry" data-testid="screen-lobby">
      <EntryAtmosphere />

      <div className="entry__top">
        <div className="entry__corner">
          <VolumeControl />
          {me && (
            <button
              type="button"
              className="entry__me entry__me--button"
              title={me.name}
              aria-label="Your record"
              data-testid="open-profile"
              onClick={() => setProfileOpen(true)}
            >
              <Avatar name={me.name} src={me.avatar} size={30} speaking={speaking} muted={muted} />
              <span className="entry__me-name">{me.name}</span>
              <LevelBadge level={level} size={20} glow />
            </button>
          )}
        </div>
      </div>

      <div className="entry__content">
        <EntryLogo home={isEmbedded() ? undefined : '/'} />

        <div className="entry__actions">
          <button
            type="button"
            className="cta cta--primary"
            data-testid="quick-play"
            disabled={disabled}
            onClick={onQuickPlay}
          >
            <span className="cta__glyph" aria-hidden>
              ♠
            </span>
            <span className="cta__label">Start game</span>
          </button>
          <button
            type="button"
            className={`cta cta--join${joinOpen ? ' cta--open' : ''}`}
            data-testid="join-game"
            disabled={disabled}
            aria-expanded={joinOpen}
            onClick={onToggleJoin}
          >
            <span className="cta__glyph" aria-hidden>
              {/* Drawn rather than typed: the chain and door characters render as colour emoji in
                  most fonts, which would sit oddly beside a plain text spade. */}
              <svg viewBox="0 0 32 32" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
                <path d="M13 19 19 13" />
                <path d="M17 8.5 20 5.5a5.5 5.5 0 0 1 7.8 7.8l-3 3" />
                <path d="M15 23.5l-3 3a5.5 5.5 0 0 1-7.8-7.8l3-3" />
              </svg>
            </span>
            <span className="cta__label">Join game</span>
          </button>

          {/* Always in the layout, whether or not it has anything in it. The slot is what keeps
              the wordmark and Start still when somebody taps Join; without it the whole screen
              lifted by about forty pixels the moment the field appeared. */}
          <div className="entry__join-slot">
            {joinOpen && (
              <div className="entry__join">
                <label className="entry__join-label" htmlFor={codeId}>
                  Table code
                </label>
                <div className="entry__join-row">
                  <input
                    ref={codeRef}
                    id={codeId}
                    className="input input--code grow"
                    value={code}
                    maxLength={5}
                    inputMode="text"
                    autoCapitalize="characters"
                    autoComplete="off"
                    spellCheck={false}
                    placeholder="ABCDE"
                    aria-label="Table code"
                    data-testid="join-code"
                    onChange={(e) => onCodeChange(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 5))}
                    onKeyDown={(e) => e.key === 'Enter' && onJoin()}
                  />
                  <Button variant="primary" testId="join" disabled={busy || code.length !== 5} onClick={onJoin}>
                    Join
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="entry__foot">
        {/* A sheet, not the /rules page: a link there tore down the lobby (and any table the
            player was sitting at) for a question they wanted answered in place. */}
        <button type="button" className="entry__foot-link" data-testid="how-to-play" onClick={() => setRulesOpen(true)}>
          How to play
        </button>
        <span className="entry__foot-sep" aria-hidden>
          ·
        </span>
        <button type="button" className="entry__foot-link" data-testid="create-private" disabled={disabled} onClick={onOpenOptions}>
          <span className="entry__foot-cog" aria-hidden>
            ⚙
          </span>
          Table rules
        </button>
        <span className="entry__foot-sep" aria-hidden>
          ·
        </span>
        {/* The pill above opens the same sheet, but only a player who already has a badge reads
            the pill as something to press. This is the plain-language route to the same place. */}
        <button type="button" className="entry__foot-link" data-testid="foot-profile" onClick={() => setProfileOpen(true)}>
          Profile
        </button>
        <span className="entry__foot-sep" aria-hidden>
          ·
        </span>
        <button type="button" className="entry__foot-link" onClick={() => setCreditsOpen(true)}>
          Credits
        </button>
      </div>

      <CreditsSheet open={creditsOpen} onClose={() => setCreditsOpen(false)} />
      <ProfileSheet open={profileOpen} onClose={() => setProfileOpen(false)} />
      <RulesSheet open={rulesOpen} onClose={() => setRulesOpen(false)} />
    </div>
  );
}
